import { createAsyncThunk } from "@reduxjs/toolkit";
import apiConfig from "constants/apiConfig";
import { fetchCampaignCollaborations, fetchCollaboration } from "services/api/campaign/collaboration";
import { ERROR } from "types";
import { CampaignCollaborationsSlice, CampaignCollaborationsFilters, Collaborations, Collaboration } from "types/campaign/collaborations";
import { generateUniqueKey, getErrorMessage, isFresh } from "utils/helpers";

export const fetchCampaignCollaborationsThunk = createAsyncThunk<
    { collaborations: Collaborations; filters: CampaignCollaborationsFilters; filterId: string; campaignId: string },
    { campaignId: string; force?: boolean; filters: CampaignCollaborationsFilters },
    { rejectValue: string }
>(
    'campaignCollaborations/fetch',
    async ({ campaignId, force, filters }, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_CAMPAIGN_COLLABORATIONS.staleTime;
            const filterId = generateUniqueKey(filters, ['page']);
            const state = getState() as { campaignCollaborations: CampaignCollaborationsSlice };

            if (
                filters.page === 1 &&
                !force &&
                isFresh(
                    state.campaignCollaborations.campaigns[campaignId]?.filtered_lists?.[filterId]?.last_fetched_at ?? null,
                    STALE_TIME
                )
            ) {
                return rejectWithValue(ERROR.CACHED);
            }

            const collaborations = await fetchCampaignCollaborations(campaignId, filters);
            return { collaborations, filters, filterId, campaignId };
        } catch {
            return rejectWithValue('Failed to fetch campaign collaborations');
        }
    }
);

export const fetchCollaborationThunk = createAsyncThunk<
    { campaignId: string, collaborationId: string, collaboration: Collaboration },
    { campaignId: string, collaborationId: string, force?: boolean },
    { rejectValue: string }
>(
    'campaignCollaborations/fetchCollaboration',
    async ({ campaignId, collaborationId, force }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { campaignCollaborations: CampaignCollaborationsSlice };
            const STALE_TIME = apiConfig.FETCH_COLLABORATION.staleTime;

            if (
                !force &&
                isFresh(
                    state.campaignCollaborations.campaigns[campaignId]?.collaborations?.[collaborationId]?.last_fetched_at ?? null,
                    STALE_TIME
                )
            ) {
                return rejectWithValue(ERROR.CACHED);
            }

            const { collaboration } = await fetchCollaboration(campaignId, collaborationId);
            return { campaignId, collaborationId, collaboration };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch collaboration!'));
        }
    }
);
